import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SortIcon from '@mui/icons-material/Sort';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useFetchLedgerJournalsQuery } from '../../redux/features/ledger/journalBrowser.apiSlice';
import { JournalBookKind, JournalFilterForm, JournalSegment } from '../../types/ledgerJournal';
import { EMPTY_FILTER_FORM, PER_PAGE_OPTIONS, SEGMENT_TITLES } from './journalExplorer.constants';
import {
  exportJournals,
  JournalUrlState,
  readUrlState,
  resolveDateRange,
  toRequestParams,
  toUrlParams,
} from './journalExplorer.helpers';
import { JournalFilters } from './components/JournalFilters';
import { JournalSummary } from './components/JournalSummary';
import { JournalCard, JournalPartyKey } from './components/JournalCard';

interface JournalExplorerProps {
  segment: JournalSegment;
}

const errorMessage = (error: unknown) =>
  (error as { data?: { message?: string } })?.data?.message || 'Failed to load journals.';

export const JournalExplorer: React.FC<JournalExplorerProps> = ({ segment }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();

  // The URL is the source of truth for what is searched; the form is what is being typed.
  const urlState = useMemo(() => readUrlState(new URLSearchParams(queryString)), [queryString]);
  const request = useMemo(() => ({ segment, params: toRequestParams(urlState, segment) }), [urlState, segment]);
  const range = useMemo(() => resolveDateRange(urlState.form.preset, urlState.form.from, urlState.form.to), [urlState]);

  const [form, setForm] = useState<JournalFilterForm>(urlState.form);
  const [linesOpen, setLinesOpen] = useState(true);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => setForm(urlState.form), [urlState]);

  const { data, error, isLoading, isFetching, refetch } = useFetchLedgerJournalsQuery(request);

  const navigate = (next: Partial<JournalUrlState>) =>
    setSearchParams(toUrlParams({ ...urlState, page: 1, ...next }, segment));

  const handleSearch = () => navigate({ form });
  // Clear the fields too: with an empty URL already, clearing it changes nothing and the effect above never runs.
  const handleReset = () => {
    setForm(EMPTY_FILTER_FORM);
    setSearchParams({});
  };
  // From what was searched (the URL), not what is half-typed in the form.
  const handleEventClick = (event: string) => navigate({ form: { ...urlState.form, event } });
  const handlePartyClick = (key: JournalPartyKey, value: number) =>
    navigate({ form: { ...EMPTY_FILTER_FORM, preset: 'all', [key]: String(value) } });

  const journals = data?.journals ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / urlState.perPage));
  const start = total === 0 ? 0 : (urlState.page - 1) * urlState.perPage + 1;
  const end = Math.min(urlState.page * urlState.perPage, total);
  const { title, subtitle } = SEGMENT_TITLES[segment];

  const handleExport = (bookType: 'xlsx' | 'csv') => {
    setExportAnchor(null);
    exportJournals(journals, `${segment.toLowerCase()}-journals-${new Date().toISOString().slice(0, 10)}`, bookType);
  };

  return (
    <Container component="main" maxWidth="xl" sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
            {data?.book ? ` · ${data.book}` : ''}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={urlState.book}
          onChange={(_, book: JournalBookKind | null) => book && navigate({ book })}
        >
          <ToggleButton value="cash" sx={{ textTransform: 'none', px: 2 }}>
            Cash
          </ToggleButton>
          <ToggleButton value="units" sx={{ textTransform: 'none', px: 2 }}>
            Units
          </ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title={urlState.sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SortIcon sx={{ transform: urlState.sortOrder === 'asc' ? 'scaleY(-1)' : 'none' }} />}
            onClick={() => navigate({ sortOrder: urlState.sortOrder === 'desc' ? 'asc' : 'desc', page: urlState.page })}
          >
            {urlState.sortOrder === 'desc' ? 'Newest' : 'Oldest'}
          </Button>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={!journals.length}
          onClick={(e) => setExportAnchor(e.currentTarget)}
        >
          Export page
        </Button>
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={() => handleExport('xlsx')}>Excel (.xlsx)</MenuItem>
          <MenuItem onClick={() => handleExport('csv')}>CSV (.csv)</MenuItem>
        </Menu>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <JournalFilters
        segment={segment}
        form={form}
        eventOptions={data?.summary.byEvent ?? []}
        loading={isFetching}
        onChange={setForm}
        onSearch={handleSearch}
        onReset={handleReset}
      />

      <JournalSummary
        summary={data?.summary}
        book={data?.book}
        range={range}
        loading={isLoading}
        activeEvent={urlState.form.event}
        onEventClick={handleEventClick}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {isLoading ? 'Loading…' : `Showing ${start}–${end} of ${total.toLocaleString('en-US')} journals`}
        </Typography>
        <Button
          size="small"
          startIcon={linesOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
          onClick={() => setLinesOpen((open) => !open)}
          disabled={!journals.length}
        >
          {linesOpen ? 'Collapse all' : 'Expand all'}
        </Button>
      </Box>

      <Box sx={{ minHeight: 4 }}>{isFetching && !isLoading && <LinearProgress />}</Box>

      {error && <Alert severity="error">{errorMessage(error)}</Alert>}

      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} variant="rounded" height={180} />
          ))}
        </Box>
      )}

      {!isLoading && !error && journals.length === 0 && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <SearchOffIcon color="disabled" sx={{ fontSize: 48 }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            No journals found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try a wider date range or fewer filters.
          </Typography>
        </Paper>
      )}

      {!isLoading && journals.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: isFetching ? 0.6 : 1 }}>
          {journals.map((journal) => (
            <JournalCard
              key={journal.journalId}
              journal={journal}
              linesOpen={linesOpen}
              onPartyClick={handlePartyClick}
            />
          ))}
        </Box>
      )}

      {total > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap', py: 2 }}>
          <Pagination
            color="primary"
            count={pageCount}
            page={Math.min(urlState.page, pageCount)}
            onChange={(_, page) => navigate({ page })}
            showFirstButton
            showLastButton
          />
          <FormControl size="small">
            <Select value={urlState.perPage} onChange={(e) => navigate({ perPage: Number(e.target.value) })}>
              {PER_PAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option} / page
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Container>
  );
};
